import * as React from 'react';
import { BoxProps, Flex, FormLabel, Input, InputProps } from '@chakra-ui/react';
import { normalizeToFullYear } from '../../../utilities/formatting';
import { IDate } from '../../../types';
import { UndoEdit } from '../../iconButtons/UndoEdit';
import { withConfirm } from '../withConfirm';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

interface IProps extends DirectionProp {
  value?: IDate;
  id?: string;
  name?: string;
  label?: string;
  dayName?: string;
  monthName?: string;
  yearName?: string;
  subText?: string;
  inputHelperText?: string;
  errorText?: string;
  hideErrorText?: boolean;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  onChangeDD: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeMM: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeYY: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'lg' | 'md' | 'sm';
  inputPropsDD?: InputProps;
  inputPropsMM?: InputProps;
  inputPropsYY?: InputProps;
  mb?: BoxProps['mb'];
  isDisabled?: boolean;
}

const DateInput: React.FC<IProps> = (props: IProps) => {
  const {
    value,
    id,
    name,
    label,
    dayName,
    monthName,
    yearName,
    subText,
    errorText,
    hideErrorText,
    forwardedRef,
    onChangeDD,
    onChangeMM,
    onChangeYY,
    onBlur,
    onFocus,
    canUndo = false,
    onUndo,
    size = 'lg',
    mb = 0,
    direction,
    inputPropsDD,
    inputPropsMM,
    inputPropsYY,
    isDisabled = false,
  } = props;

  const onBlurDD = (evt: React.FocusEvent<HTMLInputElement>) => {
    let customEvent = evt;
    // Pad single digit days with leading zero on blur
    if (evt.currentTarget.value.length > 0 && evt.currentTarget.value.length < 2) {
      const paddedValue = evt.currentTarget.value.padStart(2, '0');
      customEvent = {
        ...evt,
        target: {
          ...evt.target,
          value: paddedValue,
        },
        currentTarget: {
          ...evt.currentTarget,
          value: paddedValue,
        },
      };
      if (onChangeDD) onChangeDD(customEvent);
    }
    if (onBlur) onBlur(customEvent);
  };

  const onBlurMM = (evt: React.FocusEvent<HTMLInputElement>) => {
    let customEvent = evt;
    // Pad single digit months with leading zero on blur
    if (evt.currentTarget.value.length > 0 && evt.currentTarget.value.length < 2) {
      const paddedValue = evt.currentTarget.value.padStart(2, '0');
      customEvent = {
        ...evt,
        target: {
          ...evt.target,
          value: paddedValue,
        },
        currentTarget: {
          ...evt.currentTarget,
          value: paddedValue,
        },
      };
      if (onChangeMM) onChangeMM(customEvent);
    }
    if (onBlur) onBlur(customEvent);
  };

  const onBlurYYYY = (evt: React.FocusEvent<HTMLInputElement>) => {
    let customEvent = evt;
    // Only normalize year if user has entered a value (not empty or just whitespace)
    if (
      evt.currentTarget.value.length > 0 &&
      evt.currentTarget.value.length < 4 &&
      evt.currentTarget.value.trim() !== ''
    ) {
      const year = String(normalizeToFullYear(evt.currentTarget.value));
      customEvent = {
        ...evt,
        target: {
          ...evt.target,
          value: year,
        },
        currentTarget: {
          ...evt.currentTarget,
          value: year,
        },
      };
      if (onChangeYY) onChangeYY(customEvent);
    }
    if (onBlur) onBlur(customEvent);
  };

  const onChangeDatePart = (evt: React.ChangeEvent<HTMLInputElement>, datePart: string) => {
    // disallow any non numeric keys
    const { value } = evt.currentTarget;

    if (!/^\d*$/.test(value)) {
      return false;
    }
    switch (datePart) {
      case 'DD': {
        return onChangeDD(evt);
      }
      case 'MM': {
        return onChangeMM(evt);
      }
      case 'YY': {
        return onChangeYY(evt);
      }
    }
  };
  return (
    <CustomFormControl
      id={id}
      controlProps={{
        isInvalid: !!(!hideErrorText && errorText),
        mb,
      }}
      direction={direction}
      size={size}
      label={label}
      labelProps={{ htmlFor: `${id}-day-input` }}
      subLabel={subText}
      error={errorText}
    >
      <Flex gap={{ base: 2, lg: 4 }} mb={2}>
        <Flex flexDirection="column" flexGrow="1" width="100%" gap={0.5}>
          <Input
            id={`${id}-day-input`}
            name={dayName ?? `${name}-day-input`}
            key="day-input"
            data-selector={`${id}day-input-field`}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onChangeDatePart(evt, 'DD')}
            type="text"
            pattern="\d*"
            inputMode="numeric"
            value={value && value.DD !== null ? value.DD : ''}
            ref={forwardedRef}
            maxLength={2}
            min={1}
            max={31}
            placeholder="DD"
            onBlur={onBlurDD}
            onFocus={onFocus}
            onPaste={(evt: React.ClipboardEvent<HTMLInputElement>) => {
              evt.preventDefault();
              return false;
            }}
            size={size}
            pr={{ base: 1, lg: 4 }}
            isDisabled={isDisabled}
            {...inputPropsDD}
          />

          <FormLabel htmlFor={`${id}-day-input`} id={`${id}-day-sublabel`} fontSize="sm" fontWeight="medium" mb={0}>
            Day
          </FormLabel>
        </Flex>

        <Flex flexDirection="column" flexGrow="1" width="100%" gap={0.5}>
          <Input
            id={`${id}-month-input`}
            name={monthName ?? `${name}-month-input`}
            key="month-input"
            data-selector={`${id}month-input-field`}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onChangeDatePart(evt, 'MM')}
            type="text"
            pattern="\d*"
            inputMode="numeric"
            value={value && value.MM !== null ? value.MM : ''}
            maxLength={2}
            min={1}
            max={12}
            placeholder="MM"
            onBlur={onBlurMM}
            onFocus={onFocus}
            onPaste={(evt: React.ClipboardEvent<HTMLInputElement>) => {
              evt.preventDefault();
              return false;
            }}
            size={size}
            pr={{ base: 1, lg: 4 }}
            isDisabled={isDisabled}
            {...inputPropsMM}
          />

          <FormLabel htmlFor={`${id}-month-input`} id={`${id}-month-sublabel`} fontSize="sm" fontWeight="medium" mb={0}>
            Month
          </FormLabel>
        </Flex>

        <Flex flexDirection="column" flexGrow="1" width="100%" gap={0.5}>
          <Input
            id={`${id}-year-input`}
            name={yearName ?? `${name}-year-input`}
            key="year-input"
            data-selector={`${id}year-input-field`}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onChangeDatePart(evt, 'YY')}
            type="text"
            pattern="\d*"
            value={value && value.YYYY !== null ? value.YYYY : ''}
            maxLength={4}
            min={1900}
            placeholder="YYYY"
            onBlur={onBlurYYYY}
            onFocus={onFocus}
            onPaste={(evt: React.ClipboardEvent<HTMLInputElement>) => {
              evt.preventDefault();
              return false;
            }}
            size={size}
            pl={{ base: 3, lg: 4 }}
            pr={{ base: 1, lg: 4 }}
            isDisabled={isDisabled}
            {...inputPropsYY}
          />

          <FormLabel htmlFor={`${id}-year-input`} id={`${id}-year-sublabel`} fontSize="sm" fontWeight="medium" mb={0}>
            Year
          </FormLabel>
        </Flex>

        {canUndo && <UndoEdit id={id} onUndo={onUndo} />}
      </Flex>
    </CustomFormControl>
  );
};

const DateInputWithConfirm = withConfirm(DateInput, 'date-input');

export { DateInput, DateInputWithConfirm };
